import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import BulkImageUploader from "@/components/BulkImageUploader";
import ResultsImportModal from "@/components/dashboard/ResultsImportModal";
import { toDownloadUrl, resolveMediaUrl } from "@/lib/download";
import { Trophy, Trash2, Plus, AlertCircle, Images, X, Download, Folder, ChevronDown, ChevronRight, ListOrdered } from "lucide-react";

const LEVELS = ["national", "state", "district", "zonal", "international"];

export default function ChampionshipsManager() {
  const [items, setItems] = useState([]);
  const [galleryOf, setGalleryOf] = useState(null); // championship object
  const [resultsOf, setResultsOf] = useState(null); // championship object
  const [form, setForm] = useState({
    title: "", level: "national", discipline: "Traditional Karate",
    start_date: "", end_date: "", location: "", state: "",
    registration_deadline: "", categories: "", description: "", poster_url: "",
  });
  const [err, setErr] = useState("");
  const refresh = () => api.get("/championships").then((r) => setItems(r.data));
  useEffect(() => { refresh(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const add = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = { ...form, categories: form.categories ? form.categories.split(",").map((c) => c.trim()).filter(Boolean) : [] };
      await api.post("/championships", payload);
      setForm({ title: "", level: "national", discipline: "Traditional Karate", start_date: "", end_date: "", location: "", state: "", registration_deadline: "", categories: "", description: "", poster_url: "" });
      refresh();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
  };
  const remove = async (id) => { if (window.confirm("Remove this championship?")) { await api.delete(`/championships/${id}`); refresh(); } };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-nkf-red" /><h2 className="font-display text-xl text-nkf-blue">Championships</h2></div>
      <form onSubmit={add} className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <I wide="md:col-span-8" label="Title *" value={form.title} set={(v) => set("title", v)} required />
        <Sel wide="md:col-span-2" label="Level" value={form.level} set={(v) => set("level", v)} options={LEVELS} />
        <I wide="md:col-span-2" label="Discipline" value={form.discipline} set={(v) => set("discipline", v)} />
        <I wide="md:col-span-3" label="Start Date *" type="date" value={form.start_date} set={(v) => set("start_date", v)} required />
        <I wide="md:col-span-3" label="End Date *" type="date" value={form.end_date} set={(v) => set("end_date", v)} required />
        <I wide="md:col-span-3" label="Reg. Deadline" type="date" value={form.registration_deadline} set={(v) => set("registration_deadline", v)} />
        <I wide="md:col-span-3" label="State" value={form.state} set={(v) => set("state", v)} />
        <I wide="md:col-span-12" label="Location *" value={form.location} set={(v) => set("location", v)} required />
        <I wide="md:col-span-12" label="Categories (comma separated)" value={form.categories} set={(v) => set("categories", v)} placeholder="Cadet, Junior, Senior" />
        <I wide="md:col-span-12" label="Description" value={form.description} set={(v) => set("description", v)} />
        <div className="md:col-span-9">
          <FileUpload value={form.poster_url} onChange={(v) => set("poster_url", v)} label="Event Poster (optional)" />
        </div>
        <button className="md:col-span-3 self-end inline-flex items-center justify-center gap-1 bg-nkf-blue hover:bg-nkf-navy text-white text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded transition"><Plus className="h-3.5 w-3.5" /> Create</button>
      </form>
      {err && <div className="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
      <div className="mt-4">
        <ChampionshipsByYear items={items} onGallery={setGalleryOf} onResults={setResultsOf} onRemove={remove} />
      </div>

      {galleryOf && <ChampionshipGalleryModal championship={galleryOf} onClose={() => setGalleryOf(null)} />}
      {resultsOf && (
        <ResultsImportModal
          championshipId={resultsOf.id}
          championshipTitle={resultsOf.title}
          onClose={() => setResultsOf(null)}
          onUpdated={refresh}
        />
      )}
    </section>
  );
}

function ChampionshipsByYear({ items, onGallery, onResults, onRemove }) {
  const byYear = useMemo(() => {
    const map = {};
    items.forEach((c) => {
      const y = (c.start_date || "").slice(0, 4) || "Undated";
      (map[y] = map[y] || []).push(c);
    });
    return map;
  }, [items]);
  const years = useMemo(() => Object.keys(byYear).sort((a, b) => (b > a ? 1 : -1)), [byYear]);
  const [expanded, setExpanded] = useState(() => new Set(years));
  // Ensure the newest year is expanded whenever the list changes
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (years[0]) next.add(years[0]);
      return next;
    });
  }, [years]);

  const toggle = (y) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y); else next.add(y);
      return next;
    });
  };

  if (!items.length) return <div className="py-6 text-center text-slate-400 text-sm">No championships yet.</div>;

  return (
    <div className="space-y-3">
      {years.map((y) => {
        const isOpen = expanded.has(y);
        return (
          <div key={y} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`admin-year-folder-${y}`}>
            <button
              onClick={() => toggle(y)}
              className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition text-left"
              data-testid={`admin-year-toggle-${y}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <Folder className="h-4 w-4 text-nkf-gold" />
                <span className="font-display text-nkf-blue text-lg">{y}</span>
              </div>
              <span className="text-[11px] uppercase tracking-widest text-slate-500">
                {byYear[y].length} championship{byYear[y].length === 1 ? "" : "s"}
              </span>
            </button>
            {isOpen && (
              <ul className="divide-y divide-slate-100">
                {byYear[y].map((c) => (
                  <li key={c.id} className="py-2.5 px-4 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-3">
                      {c.poster_url && <img src={c.poster_url} alt="" className="h-10 w-10 rounded object-cover" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-nkf-blue truncate text-sm">{c.title}</div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500">{c.level} · {c.start_date} → {c.end_date}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onResults(c)}
                        data-testid={`championship-results-${c.id}`}
                        className="inline-flex items-center gap-1 text-nkf-red hover:text-nkf-navy text-[11px] uppercase tracking-widest font-bold px-2.5 py-1.5 border border-slate-200 rounded hover:border-nkf-red/40"
                        title="Upload / Manage Results"
                      >
                        <ListOrdered className="h-3.5 w-3.5" /> Results
                      </button>
                      <button
                        onClick={() => onGallery(c)}
                        data-testid={`championship-gallery-${c.id}`}
                        className="inline-flex items-center gap-1 text-nkf-blue hover:text-nkf-navy text-[11px] uppercase tracking-widest font-bold px-2.5 py-1.5 border border-slate-200 rounded hover:border-nkf-blue/40"
                        title="Manage Gallery"
                      >
                        <Images className="h-3.5 w-3.5" /> Gallery
                      </button>
                      <button onClick={() => onRemove(c.id)} className="text-red-600 hover:text-red-800 p-1.5" data-testid={`championship-delete-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ChampionshipGalleryModal({ championship, onClose }) {
  const [items, setItems] = useState([]);
  const refresh = () =>
    api.get("/media", { params: { championship_id: championship.id } })
      .then((r) => setItems(r.data));
  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    await api.delete(`/media/${id}`);
    refresh();
  };

  return (
    <div className="fixed inset-0 bg-nkf-navy/60 z-50 flex items-center justify-center p-4" data-testid="championship-gallery-modal" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-nkf-blue text-white px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-nkf-gold">Championship Gallery</div>
            <div className="font-display text-lg truncate">{championship.title}</div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="championship-gallery-close">×</button>
        </div>

        <div className="p-5 overflow-y-auto">
          <BulkImageUploader
            championshipId={championship.id}
            defaultTitle={championship.title}
            onDone={refresh}
            testid="championship-bulk-upload"
          />

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map((m) => (
              <div key={m.id} className="relative group border border-slate-200 rounded-md overflow-hidden">
                <img src={resolveMediaUrl(m.media_url)} alt={m.title || ""} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="absolute top-1.5 right-1.5 flex items-center gap-1.5">
                  <a
                    href={toDownloadUrl(m.media_url, `${(championship.title || "photo").replace(/\s+/g, "_")}_${m.id}.jpg`)}
                    className="bg-white/95 hover:bg-white text-nkf-navy p-1.5 rounded opacity-0 group-hover:opacity-100"
                    aria-label="Download"
                    data-testid={`championship-photo-download-${m.id}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => remove(m.id)}
                    className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded opacity-0 group-hover:opacity-100"
                    aria-label="Remove"
                    data-testid={`championship-photo-delete-${m.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
            {!items.length && (
              <div className="col-span-full text-center text-slate-400 text-sm py-8 border border-dashed rounded-md">
                No photos in this championship&rsquo;s gallery yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function I({ wide, label, value, set, required, type = "text", placeholder }) {
  return (
    <label className={`block ${wide || ""}`}>
      <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">{label}</span>
      <input required={required} type={type} placeholder={placeholder} value={value} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm" />
    </label>
  );
}
function Sel({ wide, label, value, set, options }) {
  return (
    <label className={`block ${wide || ""}`}>
      <span className="block text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-semibold">{label}</span>
      <select value={value} onChange={(e) => set(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-md text-sm bg-white capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
